import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, HelpCircle, Sparkles, ExternalLink, Mail, Lock, ArrowRight, CheckCircle, Loader2, ChevronDown } from 'lucide-react';
import KBIcon from './KBIcon';
import { useKB } from './KBContext';
import { useVisualTheme } from './ThemeContext';
import { libraryHelpUrl } from '../../utils/hostMode';

const LOGIN_BG = '/themes/login-bg-A.png';
const IPMI_HOME_URL = process.env.REACT_APP_IPMI_HOME_URL || 'https://ipmi.org';

const LoginScreen = () => {
  const {
    password, setPassword, handlePasswordSubmit, passwordError,
    loginVersion, setShowDisclaimerModal,
    // Magic-link member auth
    magicEmail, setMagicEmail, magicSending, magicSent, setMagicSent,
    magicError, requestMagicLink,
    showLegacyPassword, setShowLegacyPassword,
  } = useKB();
  const { visualTheme } = useVisualTheme();
  const [welcomeShown] = useState(() => new URLSearchParams(window.location.search).get('welcome') === '1');

  const bgSrc = LOGIN_BG;

  return (
    <div className="kb-login-shell" data-testid="kb-login-shell">
      <motion.aside
        className="kb-login-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        style={{ backgroundImage: `url(${bgSrc})` }}
        data-testid="kb-login-hero"
      >
        <div className="kb-login-hero-veil" />
        <img
          src="/themes/ipmi50-logo-white.png"
          alt="IPMI 50"
          className="kb-login-hero-logo"
          data-testid="kb-login-hero-logo"
        />
        <div className="kb-login-hero-content">
          <div className="kb-login-hero-eyebrow">
            <Sparkles size={14} /> Members-only intelligence library
            <span className="kb-login-beta" data-testid="kb-login-beta">BETA</span>
          </div>
          <h1 className="kb-login-hero-headline">
            The collected knowledge of the precious metals community.
          </h1>
          <p className="kb-login-hero-tagline">
            Connect. Learn. Build Relationships.
          </p>
          <div className="kb-login-hero-feats">
            <div className="kb-login-hero-feat">
              <span className="kb-login-hero-feat-n">221+</span>
              <span className="kb-login-hero-feat-l">Curated documents</span>
            </div>
            <div className="kb-login-hero-feat">
              <span className="kb-login-hero-feat-n">535</span>
              <span className="kb-login-hero-feat-l">Active members</span>
            </div>
            <div className="kb-login-hero-feat">
              <span className="kb-login-hero-feat-n">AI</span>
              <span className="kb-login-hero-feat-l">Powered search & chat</span>
            </div>
          </div>
        </div>
      </motion.aside>

      <motion.main
        className="kb-login-pane"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        <div className="kb-login-pane-inner">
          <div className="kb-login-brand">
            <KBIcon size={48} />
            <span className="kb-login-brand-text">IPMI Library</span>
          </div>

          <h2 className="kb-login-pane-headline">Welcome back.</h2>
          <p className="kb-login-pane-sub">
            {magicSent
              ? 'We sent a sign-in link to your inbox. Check your email and click the link to continue.'
              : 'Sign in with your IPMI member email and we\'ll send you a one-tap link.'}
          </p>

          {/* PRIMARY: Magic-link by email */}
          {!magicSent ? (
            <form onSubmit={requestMagicLink} className="kb-login-form" data-testid="kb-magic-form">
              <label className="kb-login-label">
                <span>IPMI member email</span>
                <div style={{ position: 'relative' }}>
                  <Mail size={16}
                    style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                             color: 'var(--kb-fg-soft, #8B96A5)', pointerEvents: 'none' }} />
                  <input
                    type="email"
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="kb-login-input"
                    data-testid="kb-magic-email-input"
                    style={{ paddingLeft: 40 }}
                    autoFocus
                    required
                  />
                </div>
              </label>
              {magicError && (
                <p className="kb-login-error" data-testid="kb-magic-error">{magicError}</p>
              )}
              <button
                type="submit"
                className="kb-login-btn"
                data-testid="kb-magic-submit"
                disabled={magicSending}
              >
                {magicSending ? <><Loader2 size={14} className="kb-spin" /> Sending link…</> :
                  'Email me a sign-in link'}
              </button>
            </form>
          ) : (
            <motion.div
              className="kb-login-magic-success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="kb-magic-success"
              style={{
                padding: '20px 22px',
                background: 'rgba(52,211,153,0.08)',
                border: '1px solid rgba(52,211,153,0.28)',
                borderRadius: 10,
                color: 'var(--kb-fg)',
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <CheckCircle size={20} style={{ color: '#34D399', flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--kb-fg)' }}>Check your email.</strong><br />
                  We sent a link to <strong>{magicEmail}</strong>. It expires in 15 minutes and can only be used once.
                  <br /><br />
                  <span style={{ color: 'var(--kb-fg-soft, #8B96A5)' }}>
                    Don't see it within a minute? Check your <strong>spam or junk</strong> folder.
                  </span>
                  <button
                    type="button"
                    onClick={() => { setMagicSent(false); setMagicEmail(''); }}
                    data-testid="kb-magic-try-again"
                    style={{
                      display: 'block', marginTop: 12, background: 'none', border: 'none',
                      color: 'var(--kb-accent, #2563EB)', cursor: 'pointer', padding: 0,
                      fontSize: 13, fontWeight: 500, textDecoration: 'underline',
                    }}
                  >
                    Try a different email →
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* SECONDARY: Collapsible legacy access-password form */}
          {!magicSent && (
            <div className="kb-login-legacy-toggle" style={{ marginTop: 18 }}>
              <button
                type="button"
                onClick={() => setShowLegacyPassword(!showLegacyPassword)}
                data-testid="kb-toggle-legacy-password"
                style={{
                  background: 'none', border: 'none', padding: '6px 0',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 13, color: 'var(--kb-fg-soft, #8B96A5)',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                <Lock size={12} />
                {showLegacyPassword ? 'Hide' : 'I have an access password instead'}
                <ChevronDown size={12} style={{
                  transition: 'transform 0.2s',
                  transform: showLegacyPassword ? 'rotate(180deg)' : 'rotate(0deg)',
                }} />
              </button>

              <AnimatePresence>
                {showLegacyPassword && (
                  <motion.form
                    onSubmit={handlePasswordSubmit}
                    className="kb-login-form"
                    data-testid="kb-auth-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ marginTop: 10, overflow: 'hidden' }}
                  >
                    <label className="kb-login-label">
                      <span>Access password</span>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        className="kb-login-input"
                        data-testid="kb-password-input"
                      />
                    </label>
                    {passwordError && (
                      <p className="kb-login-error" data-testid="kb-password-error">{passwordError}</p>
                    )}
                    <button type="submit" className="kb-login-btn-secondary" data-testid="kb-auth-submit"
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--kb-border)',
                        color: 'var(--kb-fg)',
                        padding: '11px 18px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                      }}>
                      Enter with password
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          )}

          <button
            className="kb-login-disclaimer"
            onClick={() => setShowDisclaimerModal(true)}
            data-testid="kb-auth-disclaimer"
          >
            <AlertTriangle size={12} />
            <span><strong>AI Disclaimer</strong> — Results may be inaccurate. Always verify against source.</span>
          </button>

          <div className="kb-login-pane-footer">
            <a href={libraryHelpUrl('from=login')} className="kb-login-help-link" data-testid="kb-auth-help-link">
              <HelpCircle size={12} /> How it works
            </a>
            <a
              href={IPMI_HOME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="kb-login-help-link"
              data-testid="kb-login-ipmi-home"
              title="Back to IPMI home"
            >
              <ExternalLink size={12} /> IPMI Home
            </a>
            {loginVersion && (
              <span className="kb-login-version" data-testid="kb-login-version">
                v{loginVersion} · BETA
              </span>
            )}
          </div>
        </div>
      </motion.main>

      {welcomeShown && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          data-testid="kb-welcome-toast"
          style={{
            position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(52,211,153,0.12)',
            border: '1px solid rgba(52,211,153,0.35)',
            color: '#34D399',
            padding: '12px 22px', borderRadius: 999,
            fontSize: 14, fontWeight: 600, zIndex: 99,
            display: 'flex', alignItems: 'center', gap: 10,
            backdropFilter: 'blur(12px)',
          }}
        >
          <CheckCircle size={16} /> Welcome! Signing you in…
        </motion.div>
      )}
    </div>
  );
};

export default LoginScreen;
